import {
  convertToTitleCase,
  getBillingTypeCodeMapping,
  getPolicyType,
  stringFormattingToCurrency,
} from "js/formatting";
import { useNavigate, Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { sendSmsMessage } from "service/notificationservice/sendSmsMessage";

import GeneralActionButtonsModal from "components/General/GeneralActionButtonsModal";
import getPaymentProfileApi from "service/paymentServices/GetPaymentProfile/index";
import cancelPolicyAutoPayApi from "service/paymentServices/CancelPaymentProfile";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import Toast from "components/General/Toast";
import Tooltip from "components/General/Tooltip";
import config from "config/config";

export default function ViewManageAutoPay({
  selectedPolicy,
  setManageAutoPayMode,
}) {
  let navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const [paymentProfile, setPaymentProfile] = useState();
  const [dialogState, setDialogState] = useState(false);
  const [confirmation, setConfirmation] = useState();
  const [csrUserState, setCsrUserState] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [party, setParty] = useState("");
  const [toastDetail, setToastDetail] = useState({
    type: undefined,
    title: "",
    message: "",
  });

  const errorOccurred = (title, message) => {
    setToastDetail({
      type: "error",
      title: title,
      message: message,
    });
  };

  useEffect(() => {
    function getPaymentProfile() {
      if (!selectedPolicy) {
        return errorOccurred(
          t("AuthCustomerManageAutoPay-errorTitle"),
          t("AuthCustomerManageAutoPay-errorMessage")
        );
      }
      if (selectedPolicy.billingTypeCode === "06") {
        // Credit card autopay
        setIsLoading(true);
        getPaymentProfileApi({
          policies: [selectedPolicy],
        })
          .then((response) => {
            if (response?.length > 0) {
              response?.forEach((profile) => {
                if (
                  profile?.userId !== user?.userId &&
                  profile?.paymentProfileId ===
                    selectedPolicy?.creditCard?.recurringReferenceId
                ) {
                  setParty(profile);
                  setShowTooltip(true);
                }
              });
              if (response[0]?.cardNumber) {
                setToastDetail({
                  type: undefined,
                  title: "",
                  message: "",
                });
                setPaymentProfile(response);
              } else {
                errorOccurred(
                  t("AuthCustomerManageAutoPay-errorTitle"),
                  t("AuthCustomerManageAutoPay-errorMessage")
                );
              }
            }
            setIsLoading(false);
          })
          .catch((error) => {
            console.error(
              "Server refused to connect, please try again later.",
              error
            );
            setIsLoading(false);
            errorOccurred(
              t("AuthCustomerManageAutoPay-errorTitle"),
              t("AuthCustomerManageAutoPay-errorMessage")
            );
          });
      } else if (selectedPolicy.billingTypeCode === "02") {
        //EFT auto pay
        if (selectedPolicy?.bankInfo?.bank?.accountNumber) {
          let accountNumber = selectedPolicy?.bankInfo?.bank?.accountNumber;
          setPaymentProfile([
            {
              cardNumber: accountNumber?.substring(accountNumber.length - 4),
            },
          ]);
        }
        setToastDetail({
          type: undefined,
          title: "",
          message: "",
        });
        setIsLoading(false);
      }
    }

    getPaymentProfile();
  }, [selectedPolicy]);

  const cancelAutoPay = () => {
    if (csrUser?.userId) {
      //if csr user can not cancel autopay
      setDialogState(false);
      setCsrUserState(true);
    } else {
      // cancel auto pay api call
      setIsLoading(true);
      setDialogState(false);
      cancelPolicyAutoPayApi({
        oldPolicyId: selectedPolicy?.oldPolicy,
        policyId: selectedPolicy?.contractID,
        userid: user?.userId,
        draftDay: selectedPolicy?.bankInfo?.draftDay,
      })
        .then((response) => {
          setToastDetail({
            type: "success",
            title: t("AuthCustomerCancelAutoPay-successTitle"),
            message: t("AuthCustomerCancelAutoPay-successMessage"),
          });
          setConfirmation("confirmation");
          //Send SMS Message
          if (config.COMPANY_NAME !== "everlake")
            sendSmsMessage(t("SMS-body"), config.SMS_TO_PHONE_NUMBER);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(
            "Server refused to connect, please try again later.",
            error
          );
          setIsLoading(false);
          errorOccurred(
            t("AuthCustomerCancelAutoPay-errorTitle"),
            t("AuthCustomerCancelAutoPay-errorMessage")
          );
          setDialogState(false);
        });
    }
  };

  return (
    <>
      <EverlakeLoadingOverlay isLoading={isLoading}>
        {toastDetail?.type && (
          <Toast
            title={toastDetail.title}
            message={toastDetail.message}
            type={toastDetail.type}
          />
        )}
        <div className="container mb-8">
          <h1>
            {confirmation === "confirmation"
              ? t("AuthCustomerAutoPaySummary-title")
              : t("AuthCustomerManageAutoPay-title")}
          </h1>
          <hr />
        </div>
        <div className="container mt-8">
          <p>
            {getPolicyType(selectedPolicy)} Policy #{selectedPolicy?.oldPolicy}
          </p>
        </div>

        <div className="container ui-component-spacing  mt-8">
          <div className="w-full">
            {paymentProfile && confirmation !== "confirmation" ? (
              <>
                <div className="ui-card mb-4 md:mb-6">
                  <div className="mt-2 grid grid-cols-[1fr,1fr,1fr] gap-2 px-6 py-6">
                    <div>
                      <p>{t("CustomerViewManageAutoPay-frequency")}</p>
                    </div>
                    <div className="font-bold">
                      <p>
                        {selectedPolicy?.billingMode} (
                        {selectedPolicy?.modalPremiumAmountTotal})
                      </p>
                    </div>
                    <div></div>
                    <div className="col-span-3">
                      <hr />
                    </div>
                    <div>
                      <p className="flex items-center">
                        {t("CustomerViewManageAutoPay-method")}
                        {showTooltip && (
                          <Tooltip
                            xMargin="mx-0"
                            content={`Autopay was set up with ${convertToTitleCase(
                              party?.cardHolderName
                            )}'s credit card and won't appear in your payment methods.`}
                          />
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="font-bold">
                        {selectedPolicy?.billingTypeCode === "02"
                          ? "Account "
                          : "Card "}
                        ending in{" "}
                        {paymentProfile?.length > 0
                          ? paymentProfile[0].cardNumber
                          : "XXXX"}
                      </p>
                      <p className="text-xs">
                        {getBillingTypeCodeMapping(
                          selectedPolicy?.billingTypeCode
                        )}
                      </p>
                    </div>
                    <div></div>
                    <div className="col-span-3">
                      <hr />
                    </div>
                    <div>
                      <p>{t("CustomerViewManageAutoPay-amount")}</p>
                    </div>
                    <div className="font-bold">
                      <p>
                        {stringFormattingToCurrency(
                          selectedPolicy?.modalPremiumAmountTotal
                        )}
                      </p>
                    </div>
                    <div></div>
                  </div>
                </div>
                <div className="grid grid-cols-[1fr,1fr]">
                  <div className="text-left">
                    <button
                      className="ui-btn-hyperlink"
                      aria-label="Close manage auto pay"
                      onClick={() => navigate("/payment")}
                    >
                      {t("CustomerViewManageAutoPay-back")}
                    </button>
                  </div>

                  <div className="text-right">
                    <button
                      className="ui-btn-secondary"
                      disabled={toastDetail.type === "error"}
                      onClick={() => setDialogState(true)}
                    >
                      {t("CustomerViewManageAutoPay-cancel")}
                    </button>
                    <button
                      className="ui-btn-primary"
                      disabled={toastDetail.type === "error"}
                      onClick={() => setManageAutoPayMode("edit")}
                    >
                      {t("CustomerViewManageAutoPay-edit")}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}
            {confirmation === "confirmation" && (
              <>
                <div className="ui-card relative mb-4 md:mb-6">
                  <div className="mt-2 grid grid-cols-[1fr,2fr,1fr] gap-2 px-6 py-6">
                    <div>
                      <p className="font-semibold">
                        {t("CustomerViewManageAutoPay-frequency")}
                      </p>
                    </div>
                    <div>
                      <p className="font-bold">
                        {t("CustomerViewManageAutoPay-quarterly")}
                      </p>
                      <p className="pt-2">
                        {t("CustomerViewManageAutoPay-des1")}
                      </p>
                      <p className="font-semibold pt-4">
                        <Trans
                          i18nKey="CustomerViewManageAutoPay-des2"
                          components={{
                            link1: <Link to="/payment"></Link>,
                            link2: <Link to="/payment"></Link>,
                          }}
                        ></Trans>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="mt-1 md:mt-2 pb-4">
                    <button
                      className="ui-btn-hyperlink"
                      onClick={() => navigate("/payment")}
                    >
                      {t("CustomerViewManageAutoPay-back")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </EverlakeLoadingOverlay>
      <GeneralActionButtonsModal
        title="Cancel AutoPay"
        primaryButtonText={`Yes, Cancel`}
        secondaryButtonText={"Go Back"}
        descriptionWithBoldStyle={`Are you sure you want to cancel your autopay for Policy #${selectedPolicy?.oldPolicy}?`}
        description="Your account will be updated to a quarterly paper billing cycle."
        dialogState={dialogState}
        setDialogState={setDialogState}
        primaryButtonAction={cancelAutoPay}
      />
      <GeneralPurposeDialog
        title={t("CSRErrorMessage-title")}
        desc={t("CSRErrorMessage-message")}
        state={csrUserState}
        setState={setCsrUserState}
        showCloseButton={false}
      />
    </>
  );
}
